import { createFileRoute } from "@tanstack/solid-router";
import { ErrorBoundary, For, Show, Suspense } from "solid-js";
import { FileGrid } from "~/components/files/FileGrid";
import { FileUploader } from "~/components/files/FileUploader";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { ErrorCard } from "~/components/ui/ErrorCard";
import { useListFilesQuery } from "~/lib/queries/files";
import { useProjectQuery } from "~/lib/queries/projects";
import { useGetRepresentationQuery } from "~/lib/queries/representations";
import { useSourceQuery } from "~/lib/queries/sources";

export const Route = createFileRoute(
  "/_authenticated/projects/$projectId/sources/$sourceId/representations/$representationId",
)({
  ssr: false,
  component: RepresentationDetailPage,
});

function RepresentationDetailContent() {
  const params = Route.useParams();

  const projectQuery = useProjectQuery(() => params().projectId);
  const sourceQuery = useSourceQuery(
    () => params().projectId,
    () => params().sourceId,
  );
  const representationQuery = useGetRepresentationQuery(
    () => params().projectId,
    () => params().representationId,
  );
  const filesQuery = useListFilesQuery(
    () => params().projectId,
    () => params().representationId,
  );

  const projectName = () => projectQuery.data?.name ?? "Project";
  const sourceCitation = () => sourceQuery.data?.citation ?? "Source";
  const repLabel = () =>
    representationQuery.data?.description || "Representation";

  const currentMaxSortOrder = () => {
    const files = filesQuery.data ?? [];
    return files.length > 0 ? Math.max(...files.map((f) => f.sortOrder)) : 0;
  };

  return (
    <div class="space-y-6 max-w-4xl">
      <Breadcrumb
        items={[
          { label: projectName(), to: `/projects/${params().projectId}` },
          {
            label: "Sources",
            to: `/projects/${params().projectId}/sources`,
          },
          {
            label: sourceCitation(),
            to: `/projects/${params().projectId}/sources/${params().sourceId}`,
          },
          { label: repLabel() },
        ]}
      />

      <Show
        when={representationQuery.isSuccess}
        fallback={<div>Loading representation…</div>}
      >
        {/* Header: description + provenance layers */}
        <div class="rounded-xl border border-border bg-card p-6 space-y-3">
          <h2 class="text-sm font-bold text-foreground uppercase tracking-wider border-b border-border pb-2">
            {repLabel()}
          </h2>
          <Show
            when={(representationQuery.data?.provenanceLayers ?? []).length > 0}
          >
            <div class="space-y-2">
              <span class="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                Provenance
              </span>
              <For each={representationQuery.data?.provenanceLayers ?? []}>
                {(layer) => (
                  <p class="text-sm text-muted-foreground">
                    {layer.medium}
                    {layer.callNumber ? ` · ${layer.callNumber}` : ""}
                    {layer.collection ? ` · ${layer.collection}` : ""}
                  </p>
                )}
              </For>
            </div>
          </Show>
        </div>

        {/* File management */}
        <div class="rounded-xl border border-border bg-card p-6 space-y-4">
          <h2 class="text-sm font-bold text-foreground uppercase tracking-wider border-b border-border pb-2">
            Files
          </h2>
          <FileUploader
            projectId={params().projectId}
            representationId={params().representationId}
            currentMaxSortOrder={currentMaxSortOrder()}
          />
          <FileGrid
            projectId={params().projectId}
            representationId={params().representationId}
          />
        </div>
      </Show>
    </div>
  );
}

function RepresentationDetailPage() {
  return (
    <ErrorBoundary
      fallback={(err) => (
        <ErrorCard
          title="Representation error"
          message={err?.message ?? "An unexpected error occurred."}
        />
      )}
    >
      <Suspense fallback={<div>Loading representation…</div>}>
        <RepresentationDetailContent />
      </Suspense>
    </ErrorBoundary>
  );
}
